import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { authApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import type { ActiveRoleAssignmentResponse } from '@pillar-portal/api-types';
import {
    hasInternalUsersPage,
    hasIsoUsersPage,
    hasMerchantUsersPage,
    hasCustomerUsersPage,
    hasIsoOrganizationsPage,
    hasMerchantOrganizationsPage,
    hasInternalAdminPrivileges,
    hasSuperadminPrivileges,
    hasPaymentsInfrastructurePage,
    hasCustomerOrganizationCapacity,
} from '@pillar-portal/api-types';
import {
    hasMerchantCardVaultPage,
    hasMerchantPaymentTransactionsPage,
} from '@/lib/payment-transactions-permissions';
import { OrganizationRoleSwitcher } from './OrganizationRoleSwitcher';
import { resetReactQueryAfterRoleSwitch } from './OrganizationRoleSwitcher';
import { AnchorbaseLogo } from '@/components/branding/AnchorbaseLogo';

interface MenuItem {
    label: string;
    href: string;
    requiresPermission?: (activeRoleAssignment: ActiveRoleAssignmentResponse | null) => boolean;
}

function isCustomerRole(activeRoleAssignment: ActiveRoleAssignmentResponse | null): boolean {
    return hasCustomerOrganizationCapacity(activeRoleAssignment);
}

function hasCustomerPortalPaymentsPage(
    activeRoleAssignment: ActiveRoleAssignmentResponse | null
): boolean {
    return isCustomerRole(activeRoleAssignment);
}

function hasCustomerOrganizationsPage(
    activeRoleAssignment: ActiveRoleAssignmentResponse | null
): boolean {
    return hasInternalAdminPrivileges(activeRoleAssignment);
}

function hasDownloadsPage(activeRoleAssignment: ActiveRoleAssignmentResponse | null): boolean {
    if (activeRoleAssignment === null) {
        return false;
    }
    return !isCustomerRole(activeRoleAssignment);
}

function getMenuItems(): MenuItem[] {
    const baseItems: MenuItem[] = [
        {
            label: 'Dashboard',
            href: '/dashboard',
        },
    ];

    baseItems.push(
        {
            label: 'Internal Users',
            href: '/internal-users',
            requiresPermission: hasInternalUsersPage,
        },
        {
            label: 'ISO Users',
            href: '/iso-users',
            requiresPermission: hasIsoUsersPage,
        },
        {
            label: 'Users',
            href: '/users',
            requiresPermission: hasMerchantUsersPage,
        },
        {
            label: 'Customer Users',
            href: '/customer-users',
            requiresPermission: hasCustomerUsersPage,
        },
        {
            label: 'Customer Organizations',
            href: '/organizations/customers',
            requiresPermission: hasCustomerOrganizationsPage,
        },
        {
            label: 'ISO Organizations',
            href: '/organizations/isos',
            requiresPermission: hasIsoOrganizationsPage,
        },
        {
            label: 'Merchants',
            href: '/organizations/merchants',
            requiresPermission: hasMerchantOrganizationsPage,
        },
        {
            label: 'Processors',
            href: '/payments-infrastructure/processors',
            requiresPermission: hasPaymentsInfrastructurePage,
        },
        {
            label: 'Merchant Accounts',
            href: '/payments-infrastructure/merchant-accounts',
            requiresPermission: hasPaymentsInfrastructurePage,
        },
        {
            label: 'Logical Terminals',
            href: '/payments-infrastructure/logical-terminals',
            requiresPermission: hasPaymentsInfrastructurePage,
        },
        {
            label: 'Terminal Devices',
            href: '/payments-infrastructure/terminal-devices',
            requiresPermission: hasPaymentsInfrastructurePage,
        },
        {
            label: 'Payment Transactions',
            href: '/payment-transactions',
            requiresPermission: hasMerchantPaymentTransactionsPage,
        },
        {
            label: 'Card Vault',
            href: '/card-vault',
            requiresPermission: hasMerchantCardVaultPage,
        },
        {
            label: 'Pending Payments',
            href: '/customer/payment-requests',
            requiresPermission: hasCustomerPortalPaymentsPage,
        },
        {
            label: 'Recent Payments',
            href: '/customer/payment-history',
            requiresPermission: hasCustomerPortalPaymentsPage,
        },
        {
            label: 'Payment Methods',
            href: '/customer/payment-methods',
            requiresPermission: hasCustomerPortalPaymentsPage,
        },
        {
            label: 'Legal Documents',
            href: '/legal-documents',
            requiresPermission: hasInternalAdminPrivileges,
        },
        {
            label: 'Sessions',
            href: '/sessions',
            requiresPermission: hasInternalAdminPrivileges,
        },
        {
            label: 'Download Anchorbase',
            href: '/downloads',
            requiresPermission: hasDownloadsPage,
        },
        {
            label: 'Single use tokens',
            href: '/single-use-tokens',
            requiresPermission: hasInternalAdminPrivileges,
        },
        {
            label: 'Diagnostics',
            href: '/diagnostics',
            requiresPermission: hasSuperadminPrivileges,
        },
        {
            label: 'Load Merchant Backup',
            href: '/merchant-backup',
            requiresPermission: hasInternalAdminPrivileges,
        },
        {
            label: 'Audit Log',
            href: '/audit-log',
            requiresPermission: hasSuperadminPrivileges,
        }
    );

    return baseItems;
}

export function Sidebar() {
    const log = createComponentLogger('Sidebar');
    const location = useLocation();
    const pathname = location.pathname;
    const navigate = useNavigate();
    const { user, logout: logoutStore } = useAuthStore();
    const queryClient = useQueryClient();
    const [stopImpersonationErrorMessage, setStopImpersonationErrorMessage] = useState<string | null>(
        null
    );
    // Use build-time environment variable (baked in at Docker build time)
    // VITE_ prefix makes it available to client components

    // Fetch active role assignment from current session
    const { data: activeRoleAssignment } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        enabled: !!user,
        staleTime: 30000, // Cache for 30 seconds
    });
    const { data: impersonationContext } = useQuery({
        queryKey: ['impersonationContext'],
        queryFn: async () => {
            return await authApi.getImpersonationContext();
        },
        enabled: !!user,
        staleTime: 5000,
    });

    const logoutMutation = useMutation({
        mutationFn: authApi.logout,
        onSettled: () => {
            // Clear Zustand store FIRST to immediately disable all queries that check for user
            logoutStore();
            // Remove profile query entirely to prevent any refetch attempts after logout
            runInBackground(queryClient.removeQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error removing queries', err);
            });
            // Cancel all in-flight queries to prevent them from completing after logout
            runInBackground(queryClient.cancelQueries(), (err) => {
                log.error('Error canceling queries', err);
            });
            // Clear ALL React Query cache to prevent stale data on next login
            runInBackground(queryClient.clear(), (err) => {
                log.error('Error clearing queries', err);
            });
            // Use replace instead of push to prevent back button issues
            runInBackground(navigate('/login', { replace: true }), (err) => {
                log.error('Error navigating to login', err);
            });
        },
    });

    const handleLogout = () => {
        logoutMutation.mutate();
    };

    const stopImpersonationMutation = useMutation({
        mutationFn: authApi.stopImpersonation,
        onMutate: () => {
            setStopImpersonationErrorMessage(null);
        },
        onSuccess: () => {
            resetReactQueryAfterRoleSwitch(queryClient, log);
        },
        onError: (error: unknown) => {
            const resolvedErrorMessage: string =
                error instanceof Error ? error.message : 'Failed to stop impersonation.';
            setStopImpersonationErrorMessage(resolvedErrorMessage);
        },
    });

    const menuItems = getMenuItems();
    const visibleMenuItems = menuItems.filter((item) => {
        if (!item.requiresPermission) return true;
        return item.requiresPermission(activeRoleAssignment ?? null);
    });
    const shouldShowRoleSwitcher: boolean = !isCustomerRole(activeRoleAssignment ?? null);

    return (
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col h-screen">
            <div className="p-4 border-b border-gray-200">
                <AnchorbaseLogo width={220} className="mx-auto" />
            </div>

            <div className="flex-1 overflow-y-auto py-4">
                <nav className="space-y-1 px-3">
                    {visibleMenuItems.map((item) => {
                        const isActive = pathname === item.href;
                        return (
                            <Link
                                key={item.href}
                                to={item.href}
                                className={`block px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                    isActive
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                {item.label}
                            </Link>
                        );
                    })}
                </nav>
            </div>

            {user && (
                <div className="border-t border-gray-200 p-4">
                    <div className="mb-3">
                        {impersonationContext !== null && impersonationContext !== undefined ? (
                            <p className="text-sm font-medium text-gray-900">
                                Impersonating {impersonationContext.impersonated_primary_email},{' '}
                                {impersonationContext.impersonated_role}
                                {impersonationContext.impersonated_organization_name !== null
                                    ? ` @ ${impersonationContext.impersonated_organization_name}`
                                    : ''}
                            </p>
                        ) : (
                            <>
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    {user.primary_email}
                                </p>
                                {shouldShowRoleSwitcher ? (
                                    <div className="mt-2">
                                        <OrganizationRoleSwitcher user={user} />
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                    <div className="space-y-2">
                        {impersonationContext !== null && impersonationContext !== undefined && (
                            <Button
                                onClick={() => {
                                    stopImpersonationMutation.mutate();
                                }}
                                disabled={stopImpersonationMutation.isPending}
                                className="w-full"
                            >
                                {stopImpersonationMutation.isPending
                                    ? 'Stopping...'
                                    : 'Stop impersonating'}
                            </Button>
                        )}
                        <Link to="/profile" className="block w-full">
                            <Button
                                variant={pathname === '/profile' ? 'default' : 'outline'}
                                className="w-full"
                            >
                                Profile
                            </Button>
                        </Link>
                        <Button
                            onClick={handleLogout}
                            disabled={logoutMutation.isPending}
                            variant="outline"
                            className="w-full"
                        >
                            Logout
                        </Button>
                    </div>
                </div>
            )}
            <Dialog
                open={stopImpersonationErrorMessage !== null}
                onOpenChange={(nextOpenState) => {
                    if (!nextOpenState) {
                        setStopImpersonationErrorMessage(null);
                    }
                }}
            >
                <DialogContent
                    onInteractOutside={(event) => {
                        event.preventDefault();
                    }}
                >
                    <DialogHeader>
                        <DialogTitle>Unable to stop impersonating</DialogTitle>
                        <DialogDescription>
                            because: {stopImpersonationErrorMessage ?? 'Unknown error'}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            type="button"
                            onClick={() => {
                                setStopImpersonationErrorMessage(null);
                            }}
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
